import { AutocompleteWithLabel, InputWithLabel } from '@adotel/components'
import {
  ApolloQueryResult,
  OperationVariables,
  useLazyQuery,
  useMutation
} from '@apollo/client'
import {
  Button,
  // Checkbox,
  FormControlLabel,
  IconButton,
  Modal,
  Paper,
  Slide,
  Switch,
  Typography
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { ENV } from '../../config/env'
import { PREPARE_PAYMENT_MUTATION } from '../../graphql/confirmReservePrepare'
import { GET_GATE_WAY_LIST } from '../../graphql/gateways'
import { CREDITORS_BRIEF_QUERY } from '../../graphql/walletCreditors'
import { MAKE_TRANSACTION_IN_WALLET_MUTATION } from '../../graphql/walletTransactions'
import { gatewayLogo } from '../../helpers/getGatewayLogo'
import { addCommaToPrice, removeCommaFromPrice } from '../../helpers/numbers'
import { getRoleFromStorage } from '../../helpers/userData'
import { getDirection, getTranslate, lang } from '../../localization'
import walletPagesRoleManagement from '../../staticRoleManagement/walletRoleManagement'
import {
  PreparePaymentInput,
  PreparePaymentResponse
} from '../../types/ConfirmReserve'
import { GatewayListItem } from '../../types/gateway'
import { Wallet } from '../../types/wallet'
import { CreditorBrief } from '../../types/walletCreditor'
import { TransactionInput } from '../../types/walletTransaction'
import WalletContentStructure from '../WalletContentStructure'
import {
  outstandingCreditOf,
  requiresGatewaySelection,
  settlementExceedsCredit
} from './settlement'
import useStyles from './styles/index.styles'

type ChargeWalletModalProps = {
  wallet: Wallet
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<
    ApolloQueryResult<{
      wallet: Wallet
    }>
  >
}

export default function ChargeWalletModal({
  wallet,
  isOpen,
  setIsOpen,
  refetch
}: ChargeWalletModalProps) {
  const classes = useStyles()
  const [amount, setAmount] = useState(0)
  const [manualSettlement, setmanualSettlement] = useState<boolean>(false)
  const [selectedGatewayId, setSelectedGatewayId] = useState<string | null>(
    null
  )

  const [preparePaymentInput, setPreparePaymentInput] =
    useState<PreparePaymentInput>({
      paymentType: 'Account',
      gatewayNumber: 0,
      paymentCode: wallet.walletID.toString(),
      amount: 0,
      failRoute: `${window.location.origin}/${lang}/adopay/wallet/${wallet.walletID}/details/failed`,
      successRoute: `${window.location.origin}/${lang}/adopay/wallet/${wallet.walletID}/details/successful`,
      sourceType: 'TA'
    })
  // const [agreedTerms, setAgreedTerms] = useState(false)
  const [autoCompleteLabelForCreditor, setautoCompleteLabelForCreditor] =
    useState<any>(null)

  const role = getRoleFromStorage()

  const pageConfig = walletPagesRoleManagement.find(
    (item) => item.route === '/adopay/:page'
  )

  const chargeWalletSection = pageConfig?.sections?.CHARGE_WALLET
  const manualSettlementSection = pageConfig?.sections?.MANUAL_SETTLEMENT

  const isInChargeWallet =
    chargeWalletSection?.rolesListWithReadAccess?.includes(role)

  const isInManualSettlement =
    manualSettlementSection?.rolesListWithReadAccess?.includes(role)

  const isAdmin =
    manualSettlementSection?.rolesListWithWriteAccess?.includes(role)

  useEffect(() => {
    if (!isAdmin) {
      if (isInChargeWallet) {
        setmanualSettlement(false)
      }

      if (isInManualSettlement) {
        setmanualSettlement(true)
      }
    }
  }, [])

  const [preparePayment, { loading: loadingForPrepare }] = useMutation<{
    preparePayment: PreparePaymentResponse
  }>(PREPARE_PAYMENT_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (res) => {
      const url = `${ENV.BASE_URL}/v1/payment/redirect?PaymentNumber=${res.preparePayment.paymentNumber}`
      window.location.href = url
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [getCreditors, { data: creditorsData, loading: loadingForCreditors }] =
    useLazyQuery<{
      creditorsBrief: CreditorBrief[]
    }>(CREDITORS_BRIEF_QUERY, { notifyOnNetworkStatusChange: true })

  useEffect(() => {
    if (wallet.isBilateral) {
      getCreditors({
        variables: {
          walletID: wallet.walletID
        }
      })
    }
  }, [wallet])
  const [makeTransactionInWallet, { loading }] = useMutation(
    MAKE_TRANSACTION_IN_WALLET_MUTATION,
    {
      onCompleted: (data) => {
        toast.success(getTranslate(`${data.makeTransactionInWallet.message}`))
        refetch()
        setIsOpen(false)
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  // The ceiling a settlement may repay, and whether this submission would be refused.
  // Both rules live in ./settlement so they can be tested without mounting this modal.
  const outstandingCredit = outstandingCreditOf(wallet)
  const exceedsCredit = settlementExceedsCredit(
    manualSettlement,
    amount,
    outstandingCredit
  )

  const handleSubmit = () => {
    if (!manualSettlement) {
      preparePayment({
        variables: {
          input: {
            ...preparePaymentInput,
            amount: amount
          }
        }
      })
    } else {
      const input: TransactionInput = {
        walletID: wallet.walletID,
        type: 'credit',
        status: 'completed',
        amount: amount,
        usedBalanceType: manualSettlement ? 'creditBalance' : 'walletBalance',
        description: manualSettlement ? 'manual settlement' : 'Charging wallet'
      }
      if (
        wallet.isBilateral &&
        autoCompleteLabelForCreditor &&
        autoCompleteLabelForCreditor.value
      ) {
        input.creditorID = autoCompleteLabelForCreditor.value
      }

      makeTransactionInWallet({ variables: { input } })
    }
  }

  const onCloseModal = () => {
    setIsOpen(false)
  }

  //GETWAY_LIST_QUERY
  const [getGateWayList, { data: gateWayList, loading: loadingGateways }] =
    useLazyQuery<{
      gatewayList: GatewayListItem[]
    }>(GET_GATE_WAY_LIST, {
      notifyOnNetworkStatusChange: true
    })

  useEffect(() => {
    if (wallet.isBilateral) {
      if (autoCompleteLabelForCreditor && autoCompleteLabelForCreditor.value) {
        getGateWayList({
          variables: {
            input: {
              paymentType: 'Account',
              providerCode: autoCompleteLabelForCreditor.code,
              walletType: wallet.walletType
            }
          }
        })
      }
    } else {
      getGateWayList({
        variables: {
          input: {
            paymentType: 'Account',
            providerCode: '400',
            walletType: wallet.walletType
          }
        }
      })
    }
  }, [autoCompleteLabelForCreditor])

  return (
    <div className={classes.modalContainer}>
      <Modal dir={getDirection()} open={isOpen} onClose={onCloseModal}>
        <Slide in={isOpen} direction="down">
          <Paper className={classes.paper}>
            <div className={classes.header}>
              <Typography>{getTranslate('Charge Wallet')}</Typography>
              <IconButton
                onClick={() => {
                  setIsOpen(false)
                }}>
                <CloseIcon />
              </IconButton>
            </div>
            <div className={classes.walletInfo}>
              <div className={classes.walletInfoContent}>
                <Typography className={classes.titleColor}>
                  {getTranslate('Wallet')}
                </Typography>
                <Typography>
                  {getTranslate(wallet.entity.entityName)}
                </Typography>
              </div>
              <div className={classes.walletInfoContent}>
                <Typography className={classes.titleColor}>
                  {getTranslate('Wallet Type')}
                </Typography>
                <Typography>
                  {getTranslate(wallet.entity.entityType)}
                </Typography>
              </div>
            </div>
            <div className={classes.amountSection}>
              <InputWithLabel
                label={getTranslate('Amount')}
                autoComplete="off"
                fullWidth
                value={addCommaToPrice(amount)}
                onChange={(event) => {
                  const commaLessValue = removeCommaFromPrice(
                    event.currentTarget.value
                  )
                  setAmount(Number(commaLessValue))
                }}
                inputSize="small"
              />
              <WalletContentStructure
                title={'Currency'}
                value={wallet.currency || ''}
              />
            </div>
            {wallet.isBilateral && (
              <AutocompleteWithLabel
                label={getTranslate('Creditor')}
                placeholder={getTranslate('Select Creditor')}
                includeInputInList
                fullWidth
                onChange={(event, v: any) => {
                  if (v) {
                    setautoCompleteLabelForCreditor({
                      value: v.value,
                      label: v.label,
                      code: v.code
                    })
                  } else {
                    setautoCompleteLabelForCreditor({
                      value: '',
                      label: '',
                      code: ''
                    })
                  }
                }}
                getOptionSelected={(option, value) =>
                  option.value === value.value && option.label === value.label
                }
                getOptionLabel={(option) => option.label || ''}
                options={
                  creditorsData
                    ? creditorsData?.creditorsBrief.map((item) => ({
                        label: item.entity.entityName,
                        value: item.creditorID.toString() || '',
                        code: item.entity.entityCode
                      }))
                    : []
                }
                value={autoCompleteLabelForCreditor}
                renderOption={(option) => <>{option.label}</>}
                size="small"
                inputSize="small"
                loading={loadingForCreditors}
                loadingText={getTranslate('Please wait...')}
              />
            )}
            {isAdmin && (
              <FormControlLabel
                control={
                  <Switch
                    checked={manualSettlement}
                    onChange={() => setmanualSettlement(!manualSettlement)}
                    color="primary"
                  />
                }
                label={getTranslate('Manual Settlement')}
              />
            )}
            {manualSettlement && (
              // Said before submitting, not after. The service's refusal is correct, but it
              // arrives as a toast once the amount is already typed, and a disabled button
              // with no reason is its own defect — so the ceiling is stated up front, and
              // the zero case is spelled out rather than shown as "at most 0".
              <Typography variant="caption" color="textSecondary">
                {outstandingCredit > 0
                  ? getTranslate(
                      'Settlement repays credit already drawn. At most {amount} {currency} can be settled.',
                      {
                        amount: outstandingCredit.toLocaleString(),
                        // Same `|| ''` the amount field one screen up uses: the type admits
                        // an absent currency, and a missing unit should leave the number
                        // bare rather than print "undefined" beside it.
                        currency: getTranslate(wallet.currency || '')
                      }
                    )
                  : getTranslate(
                      'This wallet has drawn no credit, so there is nothing to settle. Turn off Manual Settlement to charge it instead.'
                    )}
              </Typography>
            )}
            {!manualSettlement && (
              <>
                <Typography>{getTranslate('Payment Methods')}:</Typography>
                <div className={classes.gatewayContainer}>
                  {loadingGateways
                    ? Array.from({ length: 3 }).map((_, idx) => (
                        <div key={idx} className={classes.skeletonBank} />
                      ))
                    : gateWayList?.gatewayList.map((gateway) => {
                        const isSelected = selectedGatewayId === gateway.ID
                        return (
                          <div
                            key={gateway.ID}
                            className={`${classes.bank} ${
                              isSelected ? classes.bankSelected : ''
                            }`}
                            onClick={() => {
                              setSelectedGatewayId(gateway.ID)
                              setPreparePaymentInput({
                                ...preparePaymentInput,
                                gatewayNumber: gateway.gateWayNumber
                              })
                            }}>
                            <img
                              src={gatewayLogo(gateway.gateWayType)}
                              alt={gateway.gateWayType}
                              className={classes.bankIcon}
                            />
                          </div>
                        )
                      })}
                </div>
              </>
            )}

            {/* <Box className={classes.termsContainer}>
              <Checkbox
                checked={agreedTerms}
                onChange={(e) => setAgreedTerms(e.target.checked)}
                color="primary"
              />
              <Typography variant="body2">
                {getTranslate('I agree Terms & conditions')}
              </Typography>
            </Box> */}
            <div className={classes.buttons}>
              <Button variant="text" color="primary" onClick={onCloseModal}>
                {getTranslate('Cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={classes.saveButton}
                disabled={
                  !amount ||
                  // !agreedTerms ||
                  exceedsCredit ||
                  loading ||
                  loadingForPrepare ||
                  (wallet.isBilateral &&
                    (!autoCompleteLabelForCreditor ||
                      !autoCompleteLabelForCreditor.value)) ||
                  (requiresGatewaySelection(
                    wallet.isBilateral,
                    manualSettlement
                  ) &&
                    !selectedGatewayId)
                }
                onClick={handleSubmit}>
                {getTranslate('Pay')}
              </Button>
            </div>
          </Paper>
        </Slide>
      </Modal>
    </div>
  )
}
